<!--
  The base button component, supporting different styles and sizes.
-->
<script setup lang="ts">
import XLink from '@corp/components/XLink.vue'
import type { IconDefinition, IconSize } from '@corp/components/XIcon.vue'
import type { ButtonHTMLAttributes } from 'vue'

export interface XButtonProps {
  primary?: boolean
  secondary?: boolean
  tertiary?: boolean
  danger?: boolean
  gold?: boolean
  outlined?: boolean
  size?: 'xs' | 'sm' | 'lg' | 'xl'

  // Used for tests and accessibility.
  name?: string

  // Text content to render inside the button, or false if it has no label.
  label?: string | false

  // The type of button, which affects the behavior when inside forms, etc.
  type?: ButtonHTMLAttributes['type']

  // The name of an icon to render inside the button.
  icon?: boolean | IconDefinition

  // The size of the icon inside the button.
  iconSize?: IconSize

  // The tooltip for icon-only buttons.
  tooltip?: string | object
}

const {
  primary,
  secondary,
  tertiary,
  danger,
  gold,
  outlined,
  type = 'button',
  size = 'sm',
  label = undefined,
  icon,
  name,
} = defineProps<XButtonProps>()

const emit = defineEmits<{ click: [value: MouseEvent] }>()

const slots = useSlots()

// A button without an initial background or border.
const plain = $computed(() => !primary && !secondary && !tertiary && !gold && !outlined)

// Infers the icon from the label or name if it was provided as a Boolean.
const parsedIcon = $computed(() => icon === true ? label || name : icon)

// Whether the button has text or not.
const withText = $computed(() => Boolean(label ?? slots.default))
</script>

<template>
  <component
    :is="$attrs.href ? XLink : 'button'"
    v-tooltip="tooltip || (!withText && name)"
    :name="name"
    :class="[
      size,
      size === 'xs' || size === 'sm' ? 'text-xs' : size === 'lg' ? 'text-sm' : 'text-md',
      icon && 'inline-flex items-center justify-center',
      withText && (size === 'xs' ? 'px-2' : size === 'sm' ? 'px-6' : size === 'lg' ? 'px-10' : 'px-16'),
      {
        primary,
        secondary,
        tertiary,
        plain,
        danger,
        gold,
        outlined,
        'icon-only': !withText,
      },
    ]"
    :type="type"
    class="button group border-2 font-semibold"
    @click="emit('click', $event)"
  >
    <XIcon
      v-if="parsedIcon"
      :size="iconSize || (size === 'xs' || size === 'sm' ? 'xs' : 'sm')"
      :icon="parsedIcon"
      :class="{ 'with-text': withText }"
    />
    <span v-if="withText" class="whitespace-nowrap"><slot>{{ label }}</slot></span>
  </component>
</template>

<style lang="scss" scoped>
a {
  align-items: center;
  display: inline-flex;
  justify-content: center;
}

.button {
  --h: 3em;
  --idle: theme("colors.warm.500");
  --radius: 30px;

  background: var(--bg);
  border-color: var(--br, var(--bg));
  border-radius: var(--radius);
  color: var(--color, white);
  height: var(--h);
  outline: white;

  &:not([disabled]) {
    &:hover,
    &:focus {
      --bg: var(--bg-focus);
      --br: var(--bg);
      --color: var(--color-focus, white);
    }

    &:active {
      --bg: var(--bg-active);
      --br: var(--bg);
      --color: var(--color-active, var(--color-focus, white));
    }
  }

  &[disabled] {
    opacity: 0.6;
  }

  &:focus {
    outline: none;
  }
}

.with-text {
  margin-right: 0.5em;
}

.xs {
  --h: 2em;
}

.sm {
  --h: 40px;
}

.icon-only {
  flex-shrink: 0;
  padding: 0;
  width: var(--h);
}

.primary {
  --bg: theme("colors.blue.400");
  --bg-focus: theme("colors.blue.300");
  --bg-active: theme("colors.blue.500");
}

.gold {
  --bg: theme("colors.gold.400");
  --bg-focus: theme("colors.gold.300");
  --bg-active: theme("colors.gold.400");
}

.secondary {
  --bg: theme("colors.dark.500");
  --bg-focus: theme("colors.dark.300");
  --bg-active: theme("colors.dark.400");
}

.tertiary {
  --color: theme("colors.dark.500");
  --bg: white;
  --br: currentcolor;
  --bg-focus: theme("colors.dark.500");
  --bg-active: theme("colors.dark.400");
}

.plain {
  --bg: transparent;
  --bg-focus: theme("colors.warm.300");
  --bg-active: theme("colors.warm.400");
  --color: theme("colors.dark.500");
  --color-focus: theme("colors.dark.500");

  &.icon-only {
    --color: var(--idle);
  }

  &.highlight {
    --color: theme("colors.blue.400");
    --color-focus: theme("colors.blue.400");
  }
}

.danger {
  &.primary {
    --bg: theme("colors.red.400");
    --bg-focus: theme("colors.red.300");
    --bg-active: theme("colors.red.500");
  }

  &.tertiary {
    --color: theme("colors.red.400");
    --bg-focus: theme("colors.red.300");
    --bg-active: theme("colors.red.500");
  }

  &.plain {
    --bg-focus: theme("colors.red.100");
    --bg-active: theme("colors.red.200");
    --color-focus: theme("colors.red.300");
    --color-active: theme("colors.red.500");
  }
}

.outlined {
  --bg: transparent;
  --bg-focus: rgb(255 255 255 / 20%);
  --bg-active: rgb(255 255 255 / 20%);
  --color: currentcolor;

  border-color: currentcolor;
}

.squared {
  --radius: 4px;
}
</style>
